/**
 * What to tell someone editing a client's tax rate.
 *
 * Three states look identical in a number box and mean completely different
 * things to a bill, so the field has to say which one it is in words:
 *
 *   blank      inherit the company-wide rate
 *   0          deliberately zero-rated; the company rate does NOT apply
 *   6.35       this client overrides the company rate
 *
 * Plus exemption, which overrides all three.
 *
 * Kept out of the JSX so the wording is unit-testable, the same way
 * jobDateLabel.js is. The server resolves the real rate
 * (server/utils/taxRate.js); this only describes it.
 */

/** Format a percentage without trailing noise: 6.35 -> "6.35%", 6 -> "6%". */
export function formatRate(n) {
  if (n === null || n === undefined || n === '') return '';
  const num = typeof n === 'number' ? n : parseFloat(n);
  if (!Number.isFinite(num)) return '';
  return `${parseFloat(num.toFixed(4))}%`;
}

/**
 * Turn a stored column value into what the text input should show.
 *
 * `|| ''` would be wrong here for the same reason it is wrong on the server:
 * it turns a deliberate 0 into a blank box, which reads as "inherit" and
 * quietly re-applies the company rate to a client set to zero.
 */
export function rateToInputValue(stored) {
  if (stored === null || stored === undefined || stored === '') return '';
  return String(stored);
}

/**
 * The hint under the rate field.
 *
 * @param {{ taxExempt: boolean, rateInput: string, companyRate: number|null }} state
 * @returns {string}
 */
export function taxRateHint({ taxExempt, rateInput, companyRate }) {
  if (taxExempt) {
    return 'Tax exempt — invoices for this client are billed at 0%. '
      + 'Clear the checkbox to set a rate.';
  }

  if (rateInput === '' || rateInput === null || rateInput === undefined) {
    return companyRate === null || companyRate === undefined
      ? 'Blank means inherit the company default. No company rate is set, so this client is billed at 0%.'
      : `Blank means inherit the company default, currently ${formatRate(companyRate)}.`;
  }

  const num = parseFloat(rateInput);
  if (!Number.isFinite(num)) return 'Enter a rate between 0 and 100, or leave blank to inherit.';

  if (num === 0) {
    return 'Set to 0% for this client specifically — the company default will NOT be applied. '
      + 'Leave the box blank instead if this client should follow the company rate.';
  }

  return `This client is billed at ${formatRate(num)}, overriding the company default.`;
}

/**
 * The rate to pre-fill on a NEW invoice or estimate for a client.
 *
 * This MIRRORS server/utils/taxRate.js. The server is authoritative — it
 * resolves again on write and its answer is what gets stored — so a drift here
 * cannot produce a wrong invoice. What it CAN produce is a form showing one
 * total and the saved document carrying another, which is its own kind of
 * wrong, and the reason the exempt case is reproduced faithfully rather than
 * left to the server.
 *
 * @param {{tax_exempt?: number|boolean, default_tax_rate?: number|null}|null} client
 * @param {number|null} companyRate
 * @returns {{rate: number, exempt: boolean, source: 'exempt'|'client'|'company'|'none'}}
 */
export function resolveRateForForm(client, companyRate) {
  if (client && (client.tax_exempt === 1 || client.tax_exempt === true)) {
    return { rate: 0, exempt: true, source: 'exempt' };
  }
  // `!= null`, not truthiness: a client set to 0% must stay 0%, not inherit.
  const own = client ? toRate(client.default_tax_rate) : null;
  if (own != null) return { rate: own, exempt: false, source: 'client' };

  const co = toRate(companyRate);
  if (co != null) return { rate: co, exempt: false, source: 'company' };

  return { rate: 0, exempt: false, source: 'none' };
}

/** Shared coercion: '' / null / garbage / out-of-range all mean "not set". */
function toRate(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n) || n < 0 || n > 100) return null;
  return n;
}

/** One line explaining where a pre-filled document rate came from. */
export function documentRateNote(resolved) {
  if (!resolved) return '';
  switch (resolved.source) {
    case 'exempt':
      return 'This client is tax exempt — the rate is fixed at 0%.';
    case 'client':
      return `Using this client's rate of ${formatRate(resolved.rate)}.`;
    case 'company':
      return `Using the company default of ${formatRate(resolved.rate)}. You can change it for this document.`;
    default:
      return '';
  }
}

/** Placeholder for the empty input: shows what blank would actually mean. */
export function ratePlaceholder(companyRate) {
  return companyRate === null || companyRate === undefined
    ? 'None'
    : `${parseFloat(Number(companyRate).toFixed(4))} (company default)`;
}
